import { useQuery } from "@apollo/client";
import { GridLegacy as Grid } from "@mui/material";
import { isDisabledByRule } from "components/Omnisearch/disableFilterWhen";
import { parseOmnisearch, stringifyOmnisearch } from "components/Omnisearch/FilterPopover";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { useCallback, useMemo } from "react";

import { gql } from "../../../../generated-graphql";
import { TierIiReportKind } from "../../../../generated-graphql/graphql";
import { useOmnisearchInput } from "../../../../hooks/useOmnisearchDatagrid/useOmnisearchInput";
import { REPORT_KIND_DISABLED_WHEN } from "../AssignmentManagerTable/chemicalFacilitiesFilters";
import { FULFILLMENT_DEFAULT_REPORT_KINDS } from "../fulfillmentDefaults";
import {
  ChemicalFacilitiesDoughnutChart,
  ReportStatusId,
} from "./DoughnutChart/chemicalFacilitiesDoughnutChart";
import { KeyMetrics } from "./KeyMetrics";
import { UnverifiedTable } from "./UnverifiedTable";

const CHEMICAL_FACILITIES_SUMMARY = gql(`
  query ChemicalFacilitiesSummary($year: Int!, $reportKinds: [TierIIReportKind!]) {
    reportingSeasonSummary(year: $year, reportKinds: $reportKinds) {
      year
      noReportsTotal
      facilitiesTotal
      reportsInFilingComplete
      reportsInAwaitingVerification
      reportsInNotReporting
      reportsInProcessing
      reportsUnverified
      reportsTotal
      workflowsCountHuman
      workflowsCountError
      workflowsCountWaitForAll
      stateSummaries {
        id
        name
        unverifiedCount
        reportCount
      }
      organizationSummaries {
        id
        name
        unverifiedCount
        reportCount
      }
    }
  }
`);

export function Summary() {
  const { getOmnisearchValue, setOmnisearch, omnisearch } = useOmnisearchInput({
    isURLDriven: true,
  });

  function isValTierIiReportKind(val: any): val is TierIiReportKind {
    const validReportKinds = Object.values(TierIiReportKind);
    if (validReportKinds.includes(val)) return true;
    return false;
  }

  const reportingYearRaw = getOmnisearchValue("reportingYear");
  const year = useMemo(() => {
    return reportingYearRaw ? parseInt(reportingYearRaw, 10) : currentTierIIReportingYear;
  }, [reportingYearRaw]);

  const reportKindRaw = getOmnisearchValue("reportKind");
  const reportKinds = useMemo(() => {
    if (!reportKindRaw) return FULFILLMENT_DEFAULT_REPORT_KINDS;

    const reportKindsList = reportKindRaw.split("|");

    const validReportKinds = [];

    for (const kind of reportKindsList) {
      if (isValTierIiReportKind(kind)) {
        validReportKinds.push(kind);
      }
    }

    return validReportKinds;
  }, [reportKindRaw]);

  /**
   * Derive the currently selected status from omnisearch filters.
   * Returns the status ID if exactly one status is filtered, null otherwise.
   */
  const encampStatusRaw = getOmnisearchValue("encampStatus");
  // parseOmnisearch also reads the `nr` shorthand.
  const noReportSelected = String(parseOmnisearch(omnisearch).noReport) === "true";
  const selectedStatusId = useMemo((): ReportStatusId | null => {
    // Check for NO_REPORT filter (facilities with no report created)
    if (noReportSelected) {
      return "NO_REPORT";
    }

    // Check for single encampStatus filter
    if (encampStatusRaw && !encampStatusRaw.includes("|")) {
      const statusMap: Record<string, ReportStatusId> = {
        NOT_REPORTING: "NOT_REPORTING",
        AWAITING_VERIFICATION: "AWAITING_VERIFICATION",
        PROCESSING: "PROCESSING",
        FILING_COMPLETE: "FILING_COMPLETE",
      };
      return statusMap[encampStatusRaw] ?? null;
    }

    return null;
  }, [encampStatusRaw, noReportSelected]);

  /**
   * Handle clicks on the donut chart to filter by report status.
   * Maps status IDs to the appropriate omnisearch filter keys.
   * Clicking the same status again clears the filter.
   */
  const handleStatusClick = useCallback(
    (statusId: ReportStatusId) => {
      const reportingYear = getOmnisearchValue("reportingYear");
      const reportKind = getOmnisearchValue("reportKind");

      // If clicking the currently selected status, clear the filter
      if (statusId === selectedStatusId) {
        setOmnisearch(
          stringifyOmnisearch({
            reportingYear,
            reportKind,
          })
        );
        return;
      }

      // Start with existing filters, preserving reportingYear and reportKind
      const newFilters: Record<string, string | undefined> = {
        reportingYear,
        reportKind,
      };

      // Map status IDs to filter keys
      switch (statusId) {
        case "NOT_REPORTING":
          // Filter for encampStatus = NOT_REPORTING
          newFilters.encampStatus = "NOT_REPORTING";
          break;
        case "NO_REPORT":
          // Filter for facilities with no report created yet
          newFilters.noReport = "true";
          break;
        case "AWAITING_VERIFICATION":
          newFilters.encampStatus = "AWAITING_VERIFICATION";
          break;
        case "PROCESSING":
          newFilters.encampStatus = "PROCESSING";
          break;
        case "FILING_COMPLETE":
          newFilters.encampStatus = "FILING_COMPLETE";
          break;
      }

      // Same rule as the Filters panel, which clears Report Kind when it can't apply.
      if (isDisabledByRule(REPORT_KIND_DISABLED_WHEN, newFilters)) {
        newFilters.reportKind = undefined;
      }

      setOmnisearch(stringifyOmnisearch(newFilters));
    },
    [getOmnisearchValue, setOmnisearch, selectedStatusId]
  );

  const { data, loading } = useQuery(CHEMICAL_FACILITIES_SUMMARY, {
    fetchPolicy: "network-only",
    variables: {
      year,
      reportKinds,
    },
  });

  return (
    <Grid container spacing={3}>
      <Grid item lg={6} xs={12}>
        <ChemicalFacilitiesDoughnutChart
          data={data?.reportingSeasonSummary}
          loading={loading}
          onStatusClick={handleStatusClick}
          selectedStatusId={selectedStatusId}
        />
      </Grid>
      <Grid item md={12} lg={6}>
        <KeyMetrics data={data?.reportingSeasonSummary} loading={loading} />
      </Grid>
      <Grid item md={12} lg={6} sx={{ height: "100%" }}>
        <UnverifiedTable
          type="State"
          title="Reports by State"
          data={data?.reportingSeasonSummary.stateSummaries}
          loading={loading}
          hideRowDividers
        />
      </Grid>
      <Grid item md={12} lg={6} sx={{ height: "100%" }}>
        <UnverifiedTable
          title="Reports by Organization"
          type="Organization"
          data={data?.reportingSeasonSummary.organizationSummaries}
          loading={loading}
          hideRowDividers
        />
      </Grid>
    </Grid>
  );
}
